import { makeAutoObservable, runInAction } from 'mobx';

import { IntermediaryApplicationsApi } from 'components/intermediaryApplications/intermediaryApplications.api';
import { createPoller } from 'common/utils/polling.utils';
import {
  TEAM_MEMBER_POLLING_INTERVAL_MS,
  TEAM_MEMBER_POLLING_MAX_DURATION_MS,
} from 'components/intermediaryApplications/intermediaryApplications.constants';
import {
  CompanyProfileDto,
  MdaContactDto,
  UpdateAddressDto,
  UpdateCompanyProfileDto,
  UpdateConsentDto,
  UpdateSalespersonsDto,
} from 'components/intermediaryApplications/intermediaryApplications.dto';

class IntermediaryApplicationsStore {
  intermediaryApplicationsApi: IntermediaryApplicationsApi;

  isLoading = false;

  isMasterDialerAgreementLoading = false;

  mdaJustSigned = false;

  companyProfile: CompanyProfileDto | null = null;

  mdaContacts: MdaContactDto[] = [];

  teamMemberPoller: ReturnType<typeof createPoller>;

  constructor() {
    this.intermediaryApplicationsApi = new IntermediaryApplicationsApi();

    this.teamMemberPoller = createPoller(
      async () => {
        await this.getCompanyProfile();

        if (this.companyProfile?.contactManager?.name) {
          this.teamMemberPoller.stop();
        }
      },
      TEAM_MEMBER_POLLING_INTERVAL_MS,
      TEAM_MEMBER_POLLING_MAX_DURATION_MS,
      () => {
        runInAction(() => {
          this.mdaJustSigned = false;
        });
      },
    );

    makeAutoObservable(this, { teamMemberPoller: false }, { autoBind: true });
  }

  get isAwaitingTeamMemberAssignment() {
    return this.mdaJustSigned && !this.companyProfile?.contactManager?.name;
  }

  async getCompanyProfile() {
    this.isLoading = true;

    try {
      const companyProfile = await this.intermediaryApplicationsApi.getCompanyProfile();

      runInAction(() => {
        this.companyProfile = companyProfile;
      });

      return companyProfile;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async getMdaContacts() {
    this.isLoading = true;

    try {
      const mdaContactsDto = await this.intermediaryApplicationsApi.getMdaContacts();

      runInAction(() => {
        this.mdaContacts = mdaContactsDto;
      });

      return mdaContactsDto;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async updateCompanyProfile(body: UpdateCompanyProfileDto) {
    this.isLoading = true;

    try {
      const companyProfile = await this.intermediaryApplicationsApi.updateCompanyProfile(body);

      runInAction(() => {
        this.companyProfile = companyProfile;
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async updateAddress(body: UpdateAddressDto) {
    this.isLoading = true;

    try {
      const companyProfile = await this.intermediaryApplicationsApi.updateAddress(body);

      runInAction(() => {
        this.companyProfile = companyProfile;
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async updateSalespersons(body: UpdateSalespersonsDto) {
    this.isLoading = true;

    try {
      const companyProfile = await this.intermediaryApplicationsApi.updateSalespersons(body);

      runInAction(() => {
        this.companyProfile = companyProfile;
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async getMasterDialerAgreement() {
    this.isMasterDialerAgreementLoading = true;

    try {
      const mdaHtml = await this.intermediaryApplicationsApi.getMasterDealerAgreement();

      return mdaHtml;
    } finally {
      runInAction(() => {
        this.isMasterDialerAgreementLoading = false;
      });
    }
  }

  async signMasterDialerAgreement() {
    this.isMasterDialerAgreementLoading = true;

    try {
      const companyProfile = await this.intermediaryApplicationsApi.signMasterDealerAgreement();

      runInAction(() => {
        this.companyProfile = companyProfile;
        this.mdaJustSigned = true;
      });

      this.teamMemberPoller.start();
    } finally {
      runInAction(() => {
        this.isMasterDialerAgreementLoading = false;
      });
    }
  }

  async declineMasterDialerAgreement() {
    this.isMasterDialerAgreementLoading = true;

    try {
      const companyProfile = await this.intermediaryApplicationsApi.declineMasterDealerAgreement();

      runInAction(() => {
        this.companyProfile = companyProfile;
      });
    } finally {
      runInAction(() => {
        this.isMasterDialerAgreementLoading = false;
      });
    }
  }

  async updateConsent(consent: UpdateConsentDto) {
    this.isLoading = true;

    try {
      await this.intermediaryApplicationsApi.updateConsent(consent);
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async clearStore() {
    this.teamMemberPoller.stop();

    runInAction(() => {
      this.companyProfile = null;
      this.mdaJustSigned = false;
    });
  }
}

export default IntermediaryApplicationsStore;
